import { supabase } from '@/lib/supabase';
import type { QuotaStatus } from '@/lib/voice/quota';
import { clipFileName, type ServiceStatus } from '@/lib/voice/session';

/**
 * Voice dictation client.
 *
 * Audio goes to our own Edge Function, never to a provider directly: the Groq
 * and Anthropic keys live in Supabase secrets, and a browser-side call would
 * hand them to anyone who opens devtools.
 */

export interface TranscriptionResult {
  text: string;
  quota: QuotaStatus | null;
}

export class QuotaExhaustedError extends Error {
  constructor(public readonly quota: QuotaStatus | null) {
    super('Voice quota exhausted');
    this.name = 'QuotaExhaustedError';
  }
}

export class TranscriptionFailedError extends Error {
  constructor(
    public readonly retryable: boolean,
    /** The server has no provider key — setup was never finished. */
    public readonly unconfigured = false,
    /**
     * The request never reached the server (offline, no signal, DNS). Not an
     * outage of ours — and the device's dictation needs a connection too, so
     * the answer is to keep the audio and send it when the connection is
     * back, never to switch engines (2026-09-19, found in airplane mode).
     */
    public readonly network = false,
  ) {
    super('Transcription failed');
    this.name = 'TranscriptionFailedError';
  }
}

/** Current allowance. Null when signed out or the RPC is unavailable. */
export async function fetchVoiceQuota(): Promise<QuotaStatus | null> {
  const { data, error } = await supabase.rpc('voice_quota_status');
  if (error) return null;
  return (data as unknown as QuotaStatus | null) ?? null;
}

/**
 * Send one clip for transcription.
 *
 * `seconds` is the clip's real duration and is what gets charged — the server
 * re-checks quota before spending money and records only on success.
 */
export async function transcribeClip(
  audio: Blob,
  seconds: number,
): Promise<TranscriptionResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error('Not signed in');

  const form = new FormData();
  form.append('audio', audio, clipFileName(audio.type));
  form.append('seconds', String(Math.max(0, Math.round(seconds))));

  const base = import.meta.env.VITE_SUPABASE_URL;

  let res: Response;
  try {
    res = await fetch(`${base}/functions/v1/transcribe`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
  } catch {
    // fetch REJECTS (rather than resolving non-ok) when the request never
    // completes: offline, DNS failure, or a CORS preflight that got no usable
    // response — which is what an undeployed function looks like from the
    // browser.
    //
    // This used to escape as a raw TypeError, which is neither of the two
    // errors feedbackForError knows about, so it hit the unknown-fault branch:
    // "Dictation failed.", mic stopped, no fallback. A dead mic is the exact
    // outcome web-speech.ts exists to prevent.
    throw new TranscriptionFailedError(true, false, true);
  }

  if (res.status === 402) {
    const body = await res.json().catch(() => null) as { quota?: QuotaStatus } | null;
    throw new QuotaExhaustedError(body?.quota ?? null);
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null) as
      { retryable?: boolean; unconfigured?: boolean } | null;
    // A 404 means the Edge Function was never deployed. That is setup being
    // unfinished, not a transient fault, so it reads as `unconfigured` even
    // though the server sent no body to say so — the server isn't there to
    // send one. Both routes end at the same place: fall back to the device.
    const unconfigured = body?.unconfigured === true || res.status === 404;
    throw new TranscriptionFailedError(
      body?.retryable === true, unconfigured);
  }

  const body = await res.json() as { text?: string; quota?: QuotaStatus };
  return { text: body.text ?? '', quota: body.quota ?? null };
}

/**
 * Ask the transcription service whether it can work, before any audio is
 * recorded (GET /transcribe — answers without calling the provider or
 * spending quota). Bounded by `timeoutMs`: no answer in time is 'unknown',
 * and recording starts anyway, because the ~5-second first clip will reveal
 * a problem almost as fast.
 */
export async function checkTranscribeService(timeoutMs: number): Promise<ServiceStatus> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) return 'unknown';

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/transcribe`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
    // Not deployed at all reads as setup being unfinished, like the POST path.
    if (res.status === 404) return 'unconfigured';
    // An older deployment without the readiness check answers 405: it IS
    // there, so let the recording go ahead and let the first clip decide.
    if (res.status === 405) return 'unknown';
    if (!res.ok) return 'down';
    const body = await res.json().catch(() => null) as { ready?: boolean } | null;
    return body?.ready === true ? 'ready' : 'unconfigured';
  } catch (err) {
    // Aborted by our own timer: we simply don't know yet.
    if ((err as { name?: string } | null)?.name === 'AbortError') return 'unknown';
    // The request never completed: offline, DNS, or a CORS preflight with no
    // usable response — which is also what an undeployed function looks like.
    return 'down';
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Format a whole field after dictation stops (2026-09-30).
 *
 * Deliberately forgiving: on ANY failure — offline, function not deployed,
 * model refused, request refused — it returns the text it was given. The
 * caller treats that as "no change", so a formatting nicety can never cost the
 * designer words they said. That is why it does not throw and has no error
 * type: there is nothing a caller could usefully do with one.
 */
export async function tidyDictation(text: string, kind: TidyKind): Promise<string> {
  if (!text.trim()) return text;
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) return text;

    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/transcribe`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'content-type': 'application/json' },
      body: JSON.stringify({ text, kind }),
    });
    if (!res.ok) return text;
    const body = await res.json() as { text?: unknown };
    return typeof body.text === 'string' && body.text.trim() ? body.text : text;
  } catch {
    return text;
  }
}

/** Mirrors FieldKind in supabase/functions/_shared/tidy-rules.ts. */
export type TidyKind = 'line' | 'notes' | 'scope';
